/**
 * AbhayaNav — Custom element for the desktop vertical navigation.
 *
 * Behaviours:
 * 1. Sub-groups: only the hovered primary item's children appear (one at a time)
 * 2. Open: hover on COMPRAR toggle → panel opens; mouseleave nav → closes with delay
 * 3. Header expansion: toggle height = panel height → header__row expands naturally
 * 4. Vertical alignment: panel top aligned to toggle.offsetTop so that the
 *    first primary item appears at the same vertical position as "COMPRAR".
 */
class AbhayaNav extends HTMLElement {
    connectedCallback() {
        if (this._initialized) {
            // Re-attached (section hydration / theme editor): the observer was
            // disconnected on the way out, so hook it back up.
            if (this._panel) this._panelObserver?.observe(this._panel);
            return;
        }
        this._initialized = true;

        this._isOpen = false;
        this._isHovered = false;
        this._scrollCooldown = false;
        this._closeTimer = null;

        const isHomeAttr = this.dataset.isHome === 'true';
        const isHomePath = window.location.pathname === '/' || window.location.pathname === (window.Shopify?.routes?.root || '');
        this._isHome = isHomeAttr || isHomePath;
        console.log('[AbhayaNav] Initialized. isHome:', this._isHome);

        /** @type {HTMLButtonElement} */
        this._toggle = this.querySelector('.abhaya-nav__toggle');
        /** @type {HTMLElement} */
        this._panel = this.querySelector('.abhaya-nav__panel');
        /** @type {HTMLElement[]} */
        this._primaryItems = Array.from(this.querySelectorAll('.abhaya-nav__primary-item'));
        /** @type {HTMLElement[]} */
        this._subGroups = Array.from(this.querySelectorAll('.abhaya-nav__sub-group'));

        // ── Primary item hover: show one sub-group at a time ────────────
        this._primaryItems.forEach((item, index) => {
            item.addEventListener('mouseenter', () => this._activateItem(index));
        });

        // ── Hover toggle (closed state) → open ──────────────────────────
        if (this._toggle) {
            this._toggle.addEventListener('mouseenter', () => {
                this._cancelClose();
                this._open();
            });
        }

        // ── Mouseleave nav → close after delay ──────────────────────────
        this.addEventListener('mouseleave', () => {
            this._isHovered = false;
            this._closeTimer = setTimeout(() => {
                this._close();
            }, 200);
        });

        // ── Re-entering nav cancels pending close ────────────────────────
        this.addEventListener('mouseenter', () => {
            this._isHovered = true;
            this._cancelClose();
        });

        // ── Panel height is the source of truth for the header height ────
        // Fonts loading, zoom, viewport changes or a sub-group swap all
        // resize the panel; re-sync so the header never caps the menu.
        // Safe from feedback loops: the panel is absolutely positioned, so
        // changing the toggle height does not change the panel height.
        if (this._panel && 'ResizeObserver' in window) {
            this._panelObserver = new ResizeObserver(() => {
                if (this._isOpen) this._syncHeight();
            });
            this._panelObserver.observe(this._panel);
        }

        // ── If home page, open by default and handle scroll ───────────────
        if (this._isHome) {
            // Delay for CSS to settle
            setTimeout(() => {
                if (this._isDesktop() && window.scrollY <= 150) {
                    console.log('[AbhayaNav] Auto-opening on home load.');
                    this._open();
                }
            }, 100);

            // Re-sync height on resize to ensure it stays correct
            window.addEventListener('resize', () => {
                if (this._isOpen) {
                    this._syncHeight();
                }
            });

            // Toggle on scroll
            let lastScrollY = window.scrollY;
            window.addEventListener('scroll', () => {
                if (this._scrollCooldown) {
                    lastScrollY = window.scrollY;
                    return;
                }

                const currentScrollY = window.scrollY;
                // Ignore micro changes that could be layout shifts
                if (Math.abs(currentScrollY - lastScrollY) < 15) return;
                lastScrollY = currentScrollY;

                if (currentScrollY > 150) {
                    if (!this._isHovered && this._isOpen) {
                        console.log('[AbhayaNav] Scroll passed 150. Attempting to close.');
                        this._close();
                    }
                } else {
                    if (!this._isOpen && this._isDesktop()) {
                        console.log('[AbhayaNav] Scrolled back to top. Re-opening.');
                        this._open();
                    }
                }
            }, { passive: true });
        }
    }

    // ── O menu vertical só existe no desktop (CSS: display none < 750px).
    // Abrir no mobile deixaria data-open="true" num elemento invisível. ──
    _isDesktop() {
        return window.matchMedia('(min-width: 750px)').matches;
    }

    // ── Activate primary item and show its sub-group ─────────────────
    _activateItem(index) {
        this._activeIndex = index;

        this._primaryItems.forEach((item, i) => {
            if (i === index) item.setAttribute('data-active', '');
            else item.removeAttribute('data-active');
        });

        this._subGroups.forEach((group) => {
            group.hidden = parseInt(group.dataset.for, 10) !== index;
        });

        // Categories have different sub-item counts — the header must follow
        // the new panel height instead of staying locked to the first one.
        // Skipped while opening, so the open animation is not short-circuited.
        if (this._isOpen && !this._opening) this._syncHeight(false);
    }

    // ── Open panel ───────────────────────────────────────────────────
    _open() {
        if (this._isOpen || !this._isDesktop()) return;
        this._isOpen = true;
        this._opening = true;
        this._scrollCooldown = true;
        console.log('[AbhayaNav] _open() executed');
        setTimeout(() => { this._scrollCooldown = false; }, 500);

        // Lock current height as explicit px so CSS can transition from it
        if (this._toggle) {
            this._toggle.style.height = this._toggle.offsetHeight + 'px';
        }

        this.dataset.open = 'true';
        if (this._toggle) this._toggle.setAttribute('aria-expanded', 'true');
        if (this._panel) this._panel.removeAttribute('aria-hidden');
        this._activateItem(this._activeIndex ?? 0);

        // Wait one frame so the browser registers the starting height,
        // then set the target height — CSS transition handles the rest
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                this._opening = false;
                this._syncHeight();
            })
        );
    }

    // ── Close panel ──────────────────────────────────────────────────
    _close() {
        // Ultimate guard against closing on homepage top (desktop only —
        // no mobile o menu nem chega a abrir)
        if (this._isHome && this._isDesktop() && window.scrollY <= 150) {
            console.warn('[AbhayaNav] Blocked close attempt (isHome && scrollY <= 150)');
            return;
        }

        if (!this._isOpen) return;
        console.log('[AbhayaNav] _close() executed');

        this._isOpen = false;
        this._opening = false;
        this._scrollCooldown = true;
        setTimeout(() => { this._scrollCooldown = false; }, 500);

        this.dataset.open = 'false';
        if (this._toggle) this._toggle.setAttribute('aria-expanded', 'false');
        if (this._panel) this._panel.setAttribute('aria-hidden', 'true');
        this._clearHeight();
    }

    // ── Sync toggle height to panel height (expands header row) ──────
    // Also aligns panel top to toggle offsetTop so the first primary
    // item appears at the same vertical position as "COMPRAR".
    _syncHeight(animate = true) {
        if (!this._panel || !this._toggle) return;

        const panelH = this._panel.offsetHeight;
        if (panelH <= 0) return;

        const target = panelH + 'px';
        if (this._toggle.style.height === target) return;

        // Expand toggle (in-flow) to panel height → header row grows.
        // Swapping categories must snap instantly, otherwise the header
        // would wobble through a 600ms animation on every hover.
        if (animate) {
            this._toggle.style.height = target;
        } else {
            const previousTransition = this._toggle.style.transition;
            this._toggle.style.transition = 'none';
            this._toggle.style.height = target;
            void this._toggle.offsetHeight; // flush the layout without transition
            this._toggle.style.transition = previousTransition;
        }

        // Align panel vertically: start at the toggle's own top within .abhaya-nav
        // so the first item sits at the same Y as the COMPRAR text.
        this._panel.style.top = this._toggle.offsetTop + 'px';
    }

    _clearHeight() {
        if (this._toggle) this._toggle.style.height = '';
        if (this._panel) this._panel.style.top = '';
    }

    disconnectedCallback() {
        this._panelObserver?.disconnect();
        this._cancelClose();
    }

    _cancelClose() {
        if (this._closeTimer) {
            clearTimeout(this._closeTimer);
            this._closeTimer = null;
        }
    }
}

customElements.define('abhaya-nav', AbhayaNav);
